/* Lazy Day Crafts — Ready-to-Press browser. Vanilla ES2020, no dependencies.
 * Hierarchy: theme (?cat=) → collection (&col=) → style (&style=, Shopify tag URL) — plus search (?q=) and sort.
 * Upgrades the server-rendered page in sections/ldc-rtp-browser.liquid; grids come from the Section Rendering
 * API (sections/ldc-rtp-grid.liquid) on real collection/search URLs, so Shopify does paging, sorting and tag filters.
 * window.LDC_RTP_ADAPTER may override { gridUrl, productUrl, suggestUrl, addToCart } (tests only).
 */
(() => {
  'use strict';
  const root = document.querySelector('[data-ldc-rtp]');
  if (!root) return;

  const $ = (s, el = root) => el.querySelector(s);
  const $$ = (s, el = root) => Array.from(el.querySelectorAll(s));
  const A = window.LDC_RTP_ADAPTER || {};
  const ROOT_URL = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const PER = 24; // page size of sections/ldc-rtp-grid.liquid
  const SORTS = ['best-selling', 'created-descending', 'price-ascending', 'title-ascending'];
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const master = root.dataset.master;
  const grid = $('[data-ldc-grid]');
  const moreBtn = $('[data-ldc-more]');
  const countEl = $('[data-ldc-count]');
  const titleEl = $('[data-ldc-title]');
  const crumbsEl = $('[data-ldc-crumbs]');
  const sortEl = $('[data-ldc-sort]');
  const emptyEl = $('[data-ldc-empty]');
  const errorEl = $('[data-ldc-error]');
  const siblingsEl = $('[data-ldc-siblings]');
  const stylesEl = $('[data-ldc-styles]');
  const progressEl = $('[data-ldc-progress]');
  const progressBar = $('[data-ldc-progress-bar]');
  const fmt = new Intl.NumberFormat('en-US');
  const money = (cents) => '$' + (cents / 100).toFixed(2);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const handleize = (s) => String(s).toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const titleize = (s) => String(s).replace(/(^|[\s-])([a-z])/g, (m, a, b) => a + b.toUpperCase());
  const ICON = {
    chevron: '<path d="M9 5l7 7-7 7"/>',
    grid: '<rect x="3.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.5"/>',
  };
  const icon = (n) => `<svg class="ldc-icon" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${ICON[n]}</svg>`;

  /* ---------------- index of the hierarchy (from the server-rendered theme panels) ---------------- */
  const panels = Object.fromEntries($$('[data-ldc-panel]').map((p) => [p.dataset.ldcPanel, p]));
  const colIndex = {};
  $$('.ldc-panel .ldc-col__link[data-ldc-col]').forEach((a) => {
    if (colIndex[a.dataset.ldcCol]) return; // a collection listed in two themes keeps its first home
    const img = a.querySelector('img');
    colIndex[a.dataset.ldcCol] = {
      cat: a.dataset.ldcCat,
      name: $('.ldc-col__name', a).textContent.trim(),
      styles: (a.dataset.styles || '').split('|').map((s) => s.trim()).filter(Boolean),
      img: img ? img.getAttribute('src') : '',
    };
  });

  const state = { cat: 'all', col: '', style: '', q: '', sort: 'best-selling', page: 1, pages: 1, handle: master, loading: false };
  let ctrl = null;
  let autoLoad = false;

  const track = (event, data = {}) => {
    (window.dataLayer = window.dataLayer || []).push({ event, ...data });
    try { window.Shopify && Shopify.analytics && Shopify.analytics.publish(event, data); } catch (e) { /* noop */ }
  };

  /* ---------------- sticky offset under Dawn's header ---------------- */
  const header = document.querySelector('.section-header');
  const bar = $('[data-ldc-bar]');
  const setTop = () => {
    const b = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    document.documentElement.style.setProperty('--ldc-sticky-top', `${Math.round(b)}px`);
    if (bar) bar.classList.toggle('is-stuck', bar.getBoundingClientRect().top <= b + 1);
  };
  let rafId = 0;
  window.addEventListener('scroll', () => { cancelAnimationFrame(rafId); rafId = requestAnimationFrame(setTop); }, { passive: true });
  window.addEventListener('resize', setTop);
  setTop();
  const stickyTop = () => parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ldc-sticky-top'), 10) || 0;

  /* ---------------- URLs ---------------- */
  const gridUrl = (s, page) => {
    if (A.gridUrl) return A.gridUrl(s, page);
    if (s.q) {
      const p = new URLSearchParams({ q: s.q, type: 'product', 'options[prefix]': 'last', section_id: 'ldc-rtp-grid', page });
      return `${ROOT_URL}search?${p}`;
    }
    const p = new URLSearchParams({ section_id: 'ldc-rtp-grid', sort_by: s.sort, page });
    const tag = s.style ? `/${encodeURIComponent(handleize(s.style))}` : '';
    return `${ROOT_URL}collections/${encodeURIComponent(s.handle)}${tag}?${p}`;
  };

  const writeUrl = (push) => {
    const u = new URL(location.href);
    ['cat', 'col', 'style', 'q', 'sort'].forEach((k) => u.searchParams.delete(k));
    if (state.cat && state.cat !== 'all' && !state.q) u.searchParams.set('cat', state.cat);
    if (state.col) u.searchParams.set('col', state.col);
    if (state.style) u.searchParams.set('style', handleize(state.style));
    if (state.q) u.searchParams.set('q', state.q);
    const themeDefault = (panels[state.cat] && panels[state.cat].dataset.feedSort) || 'best-selling';
    if (state.sort !== themeDefault) u.searchParams.set('sort', state.sort);
    history[push ? 'pushState' : 'replaceState']({ ldc: true }, '', u);
  };

  /* ---------------- view sync ---------------- */
  function scrollRowTo(row, el) {
    if (!row || !el) return;
    const left = el.offsetLeft - row.offsetLeft - row.clientWidth / 2 + el.clientWidth / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: REDUCED ? 'auto' : 'smooth' });
  }

  function syncNav() {
    $$('[data-ldc-cat], [data-ldc-col]').forEach((a) => {
      if (a.closest('[data-ldc-siblings]') || a.closest('[data-ldc-crumbs]')) return;
      const on = a.dataset.ldcCol ? a.dataset.ldcCol === state.col : a.dataset.ldcCat === (state.q ? '' : state.cat);
      a.classList.toggle('is-active', on);
      if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
    });
    const view = state.q || state.col ? '' : state.cat;
    Object.entries(panels).forEach(([k, p]) => p.classList.toggle('is-active', k === view));
    $$('.ldc-tree__item[data-ldc-nav-cat]').forEach((li) => {
      if (li.dataset.ldcNavCat === state.cat) toggleSub(li.querySelector('.ldc-tree__toggle'), true);
    });
    const pill = $(`.ldc-pill[data-ldc-cat="${CSS.escape(state.q ? 'all' : state.cat)}"]`);
    scrollRowTo($('.ldc-bar__themes'), pill && pill.parentElement);
    if (sortEl) { sortEl.value = state.sort; sortEl.disabled = !!state.q; }
    renderSiblings();
    renderStyles();
    setCrumbs();
  }

  function renderSiblings() {
    const p = panels[state.cat];
    if (!state.col || !p || state.q) { siblingsEl.hidden = true; siblingsEl.innerHTML = ''; return; }
    const items = $$('.ldc-cols > .ldc-col', p).map((li) => {
      const c = li.cloneNode(true);
      c.hidden = false;
      const a = $('a', c);
      a.classList.toggle('is-active', a.dataset.ldcCol === state.col);
      return c;
    });
    const all = document.createElement('li');
    all.className = 'ldc-col ldc-siblings__all';
    all.innerHTML = `<a class="ldc-col__link" href="?cat=${esc(state.cat)}" data-ldc-cat="${esc(state.cat)}"><span class="ldc-col__img">${icon('grid')}</span><span class="ldc-col__name">All ${esc(p.dataset.short || p.dataset.title)}</span></a>`;
    siblingsEl.replaceChildren(all, ...items);
    siblingsEl.style.setProperty('--accent', p.style.getPropertyValue('--accent'));
    siblingsEl.style.setProperty('--on', p.style.getPropertyValue('--on'));
    siblingsEl.hidden = items.length < 2;
    requestAnimationFrame(() => { const act = $('.is-active', siblingsEl); scrollRowTo(siblingsEl, act && act.closest('.ldc-col')); });
  }

  function renderStyles() {
    const c = state.col && colIndex[state.col];
    if (!c || !c.styles.length || state.q) { stylesEl.hidden = true; stylesEl.innerHTML = ''; return; }
    const chip = (val, label) => `<button type="button" class="ldc-chip" data-ldc-style="${esc(val)}" aria-pressed="${handleize(val) === handleize(state.style)}">${esc(label)}</button>`;
    stylesEl.innerHTML = '<span class="ldc-styles__label">Style</span>' + chip('', 'All') + c.styles.map((s) => chip(s, titleize(s))).join('');
    stylesEl.hidden = false;
  }

  function setCrumbs() {
    const parts = [];
    const p = panels[state.cat];
    if (state.q) parts.push([null, `Results for “${state.q}”`]);
    else {
      if (state.cat !== 'all' && p) parts.push([state.col ? `?cat=${state.cat}` : null, p.dataset.title, { ldcCat: state.cat }]);
      if (state.col && colIndex[state.col]) parts.push([state.style ? `?col=${state.col}` : null, colIndex[state.col].name, { ldcCol: state.col, ldcCat: state.cat }]);
      if (state.style) parts.push([null, titleize(state.style)]);
    }
    crumbsEl.hidden = !parts.length;
    crumbsEl.innerHTML = '';
    if (!parts.length) return;
    const add = (href, text, data) => {
      const el = document.createElement(href ? 'a' : 'span');
      if (href) { el.href = href; Object.assign(el.dataset, data); } else el.setAttribute('aria-current', 'page');
      el.textContent = text;
      crumbsEl.append(el);
    };
    add(root.dataset.pageUrl, 'All themes', { ldcCat: 'all' });
    parts.forEach(([href, text, data]) => { crumbsEl.insertAdjacentHTML('beforeend', icon('chevron')); add(href, text, data || {}); });
  }

  function viewTitle(payload) {
    const p = panels[state.cat];
    if (state.q) return `“${state.q}”`;
    if (state.col) return (colIndex[state.col] || {}).name || (payload && payload.dataset.title) || '';
    if (state.cat !== 'all' && p) {
      if (p.dataset.feedSort) return p.dataset.title;
      return p.dataset.feed ? `All ${p.dataset.title}` : `${p.dataset.title} — ${(payload && payload.dataset.title) || ''}`;
    }
    return 'All designs';
  }

  const skeletons = (n = 6) => Array.from({ length: n }, () => '<li class="ldc-card ldc-card--skeleton" aria-hidden="true"><div class="ldc-card__media"></div><p></p><p></p></li>').join('');

  /* ---------------- fetching ---------------- */
  async function fetchPayload(s, page, signal) {
    const res = await fetch(gridUrl(s, page), { signal, headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(res.status);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    return doc.querySelector('.ldc-payload');
  }

  async function load({ append = false } = {}) {
    if (ctrl) ctrl.abort();
    ctrl = new AbortController();
    const page = append ? state.page + 1 : 1;
    state.loading = true;
    errorEl.hidden = true;
    root.classList.add('is-loading');
    if (!append) { titleEl.textContent = viewTitle(null); countEl.textContent = ''; grid.innerHTML = skeletons(); emptyEl.hidden = true; progressEl.textContent = ''; }
    else if (moreBtn) moreBtn.textContent = 'Loading…';
    try {
      const payload = await fetchPayload(state, page, ctrl.signal);
      const items = payload ? Array.from(payload.querySelectorAll('[data-ldc-items] > li')) : [];
      const seen = new Set(append ? $$('.ldc-card[data-handle]', grid).map((li) => li.dataset.handle) : []);
      const fresh = items.filter((li) => !seen.has(li.dataset.handle));
      if (!append) grid.innerHTML = '';
      grid.append(...fresh);
      state.page = page;
      state.pages = payload ? +payload.dataset.pages || 1 : 1;
      const total = payload ? +payload.dataset.total || 0 : 0;
      const shown = $$('.ldc-card[data-handle]', grid).length;
      const more = state.page < state.pages;
      countEl.textContent = state.q ? (shown ? `${fmt.format(shown)}${more ? '+' : ''} designs found` : '') : `${fmt.format(total)} designs`;
      titleEl.textContent = viewTitle(payload);
      // A page of non-premade search hits can be empty while later pages aren't — keep going.
      if (!fresh.length && more) { state.loading = false; return load({ append: true }); }
      emptyEl.hidden = shown > 0;
      if (!shown) {
        $('[data-ldc-empty-title]').textContent = state.q ? `No designs match “${state.q}” yet.` : 'Nothing here yet.';
        if (state.q) track('ldc_rtp_search_no_results', { search_term: state.q });
      }
      if (moreBtn) {
        moreBtn.hidden = !more;
        moreBtn.textContent = 'Show more designs';
        moreBtn.href = gridUrl(state, state.page + 1).replace(/[?&]section_id=ldc-rtp-grid/, '');
      }
      const denom = state.q ? shown : Math.max(total, shown);
      progressEl.textContent = shown ? `Showing ${fmt.format(shown)} of ${fmt.format(denom)}${state.q && more ? '+' : ''}` : '';
      if (progressBar) progressBar.style.width = `${denom ? Math.min(100, (shown / denom) * 100) : 100}%`;
    } catch (e) {
      if (e.name === 'AbortError') return;
      errorEl.hidden = false;
      if (!append) { grid.innerHTML = ''; countEl.textContent = ''; progressEl.textContent = ''; if (moreBtn) moreBtn.hidden = true; }
      if (moreBtn) moreBtn.textContent = 'Show more designs';
    } finally {
      state.loading = false;
      root.classList.remove('is-loading');
    }
  }

  /* ---------------- state transitions ---------------- */
  function resolveHandle() {
    if (state.col) return state.col;
    const p = panels[state.cat];
    if (state.cat !== 'all' && p) {
      if (p.dataset.feed) return p.dataset.feed;
      const first = p.querySelector('.ldc-col__link[data-ldc-col]');
      if (first) return first.dataset.ldcCol;
    }
    return master;
  }

  function apply({ cat, col, style, q, sort }, { push = false, scroll = false, fetch: doFetch = true } = {}) {
    if (q !== undefined) state.q = q;
    if (col !== undefined) { if ((col || '') !== state.col) state.style = ''; state.col = col || ''; }
    if (style !== undefined) state.style = style || '';
    if (cat !== undefined || (state.col && colIndex[state.col])) {
      let next = cat !== undefined && (panels[cat] || cat === 'all') ? cat : state.cat || 'all';
      if (state.col && colIndex[state.col] && (cat === undefined || cat === 'all' || !panels[cat])) next = colIndex[state.col].cat;
      // A new theme gets its own default order (New = newest) unless the URL says otherwise.
      if (next !== state.cat && !sort) state.sort = (panels[next] && panels[next].dataset.feedSort) || 'best-selling';
      state.cat = next;
    }
    if (sort && SORTS.includes(sort)) state.sort = sort;
    if (state.q) { state.col = ''; state.style = ''; }
    if (state.style && !(colIndex[state.col] || { styles: [] }).styles.some((s) => handleize(s) === handleize(state.style))) state.style = '';
    state.handle = resolveHandle();
    autoLoad = false;
    syncNav();
    writeUrl(push);
    if (doFetch) load();
    if (scroll) {
      const target = state.col || state.q ? $('.ldc-results') : state.cat === 'all' ? $('.ldc-layout') : panels[state.cat];
      const barH = bar && getComputedStyle(bar).display !== 'none' ? bar.offsetHeight : 0;
      const y = target.getBoundingClientRect().top + scrollY - stickyTop() - barH - 12;
      if (Math.abs(y - scrollY) > 40) scrollTo({ top: y, behavior: REDUCED ? 'auto' : 'smooth' });
    }
  }

  /* ---------------- events ---------------- */
  root.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-ldc-cat], a[data-ldc-col]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
    e.preventDefault();
    const col = a.dataset.ldcCol || '';
    const cat = a.dataset.ldcCat || (colIndex[col] || {}).cat || 'all';
    if (sheet.open) sheet.close();
    clearSearch(false);
    if (col) track('ldc_rtp_collection_select', { collection: col, category: cat });
    else track('ldc_rtp_category_select', { category: cat });
    apply({ cat, col, q: '', style: '' }, { push: true, scroll: true });
  });

  stylesEl.addEventListener('click', (e) => {
    const b = e.target.closest('[data-ldc-style]');
    if (!b) return;
    track('ldc_rtp_style_select', { collection: state.col, style: b.dataset.ldcStyle });
    apply({ style: b.dataset.ldcStyle }, { push: true });
  });

  sortEl && sortEl.addEventListener('change', () => apply({ sort: sortEl.value }));

  moreBtn && moreBtn.addEventListener('click', (e) => {
    e.preventDefault();
    if (state.loading) return;
    autoLoad = true; // after the first click, keep loading as the shopper scrolls
    load({ append: true });
  });
  if (moreBtn && 'IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && autoLoad && !state.loading && state.page < state.pages) load({ append: true });
    }, { rootMargin: '700px' }).observe(moreBtn);
  }
  // server-rendered first page: e.g. 12 rendered -> next fetch is page 1 (duplicates dropped); 36 -> page 2
  state.pages = Math.ceil((+root.dataset.total || 0) / PER) || 1;
  state.page = Math.floor($$('.ldc-card', grid).length / PER);

  const retry = $('[data-ldc-retry]');
  retry && retry.addEventListener('click', () => load());
  window.addEventListener('popstate', () => fromUrl(false));

  /* ---------------- "Fresh off the press" shelf (lazy) ---------------- */
  const shelf = $('[data-ldc-shelf]');
  if (shelf) {
    const fill = async () => {
      try {
        const payload = await fetchPayload({ handle: master, sort: shelf.dataset.ldcShelf, q: '', style: '' }, 1);
        const items = payload ? Array.from(payload.querySelectorAll('[data-ldc-items] > li')).slice(0, 12) : [];
        if (items.length) $('[data-ldc-shelf-list]', shelf).replaceChildren(...items); else shelf.hidden = true;
      } catch (e) { shelf.hidden = true; }
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((en) => { if (en[0].isIntersecting) { io.disconnect(); fill(); } }, { rootMargin: '400px' });
      io.observe(shelf);
    } else fill();
  }

  /* ---------------- rail tree ---------------- */
  function toggleSub(btn, force) {
    if (!btn) return;
    const open = force !== undefined ? force : btn.getAttribute('aria-expanded') !== 'true';
    if (open) $$('.ldc-tree__toggle[aria-expanded="true"]').forEach((b) => { if (b !== btn) toggleSub(b, false); });
    btn.setAttribute('aria-expanded', String(open));
    const sub = document.getElementById(btn.getAttribute('aria-controls'));
    if (sub) sub.hidden = !open;
  }
  root.addEventListener('click', (e) => { const b = e.target.closest('.ldc-tree__toggle'); if (b) toggleSub(b); });

  /* ---------------- collection filter inside a theme ---------------- */
  root.addEventListener('input', (e) => {
    const f = e.target.closest('[data-ldc-colfilter]');
    if (!f) return;
    const v = f.value.trim().toLowerCase();
    $$('.ldc-cols > .ldc-col', f.closest('.ldc-panel')).forEach((li) => { li.hidden = !!v && !li.dataset.name.includes(v); });
  });

  /* ---------------- Browse sheet: themes left, collections right ---------------- */
  const sheet = $('[data-ldc-sheet]');
  const find = $('[data-ldc-sheet-find]', sheet);
  let sheetOpener = null;
  const tabs = $$('[data-ldc-tab]', sheet);
  function selectTab(slug, focus) {
    const t = tabs.find((x) => x.dataset.ldcTab === slug) || tabs[0];
    if (!t) return;
    tabs.forEach((x) => { const on = x === t; x.setAttribute('aria-selected', String(on)); x.tabIndex = on ? 0 : -1; });
    $$('.ldc-nav2__pane', sheet).forEach((p) => { p.hidden = p.id !== t.getAttribute('aria-controls'); });
    $('.ldc-nav2__panes', sheet).scrollTop = 0;
    if (focus) t.focus();
  }
  sheet.addEventListener('click', (e) => {
    const t = e.target.closest('[data-ldc-tab]');
    if (t) selectTab(t.dataset.ldcTab);
    if (e.target === sheet) sheet.close();
  });
  sheet.addEventListener('keydown', (e) => {
    const t = e.target.closest('[data-ldc-tab]');
    if (!t || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const i = tabs.indexOf(t);
    const n = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (i + (e.key === 'ArrowDown' ? 1 : -1) + tabs.length) % tabs.length;
    selectTab(tabs[n].dataset.ldcTab, true);
  });
  $$('[data-ldc-sheet-open]').forEach((b) => b.addEventListener('click', () => {
    sheetOpener = b;
    selectTab(panels[state.cat] && !panels[state.cat].dataset.feedSort ? state.cat : '');
    sheet.showModal();
    track('ldc_rtp_browse_open', {});
  }));
  $('[data-ldc-sheet-close]', sheet).addEventListener('click', () => sheet.close());
  sheet.addEventListener('close', () => { if (find && find.value) { find.value = ''; filterSheet(''); } if (sheetOpener) sheetOpener.focus({ preventScroll: true }); });
  function filterSheet(v) {
    sheet.classList.toggle('is-finding', !!v);
    let any = false;
    $$('.ldc-nav2__pane', sheet).forEach((pane) => {
      let n = 0;
      $$('.ldc-col', pane).forEach((li) => { const hit = !v || li.dataset.name.includes(v); li.hidden = !hit; n += hit ? 1 : 0; });
      pane.classList.toggle('is-empty', !n);
      any = any || n > 0;
    });
    let none = $('.ldc-sheet__none', sheet);
    if (v && !any) {
      if (!none) { none = document.createElement('p'); none.className = 'ldc-sheet__none'; $('.ldc-nav2__panes', sheet).prepend(none); }
      none.textContent = `No collection called “${v}”. Close this and search all designs instead.`;
    } else if (none) none.remove();
    if (!v) { const cur = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0]; if (cur) selectTab(cur.dataset.ldcTab); }
  }
  find && find.addEventListener('input', () => filterSheet(find.value.trim().toLowerCase()));

  /* ---------------- predictive search ---------------- */
  const form = $('[data-ldc-search]');
  const input = form && $('.ldc-search__input', form);
  const box = form && $('.ldc-suggest', form);
  const clearBtn = form && $('[data-ldc-search-clear]', form);
  let sTimer = 0, sCtrl = null, active = -1;
  const isPremade = (p) => (p.tags || []).some((t) => t === 'ldc-premade' || t === 'Premade DTF') || /-dtf-transfer(-\d+)?$/.test(p.handle);

  function closeSuggest() { if (!box) return; box.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; }
  function clearSearch(reload = true) {
    if (!input) return;
    input.value = ''; clearBtn.hidden = true; closeSuggest();
    if (reload && state.q) apply({ q: '' }, { push: true });
  }

  async function suggest(q) {
    if (sCtrl) sCtrl.abort();
    sCtrl = new AbortController();
    const url = A.suggestUrl ? A.suggestUrl(q)
      : `${ROOT_URL}search/suggest.json?${new URLSearchParams({ q, 'resources[type]': 'collection,product', 'resources[limit]': 10, 'resources[options][fields]': 'title,product_type,tag,variants.title' })}`;
    try {
      const res = await fetch(url, { signal: sCtrl.signal });
      const r = (await res.json()).resources.results;
      const cols = (r.collections || []).filter((c) => colIndex[c.handle]).slice(0, 4);
      const prods = (r.products || []).filter(isPremade).slice(0, 5);
      let i = 0;
      const opt = (inner, attrs, cls = '') => `<a class="ldc-suggest__opt${cls}" role="option" id="ldc-opt-${i++}" ${attrs}>${inner}</a>`;
      let html = '';
      if (cols.length) {
        html += '<p class="ldc-suggest__group" aria-hidden="true">Collections</p>' + cols.map((c) => {
          const ci = colIndex[c.handle];
          const theme = panels[ci.cat] ? panels[ci.cat].dataset.title : '';
          return opt(`<span class="ldc-suggest__thumb">${ci.img ? `<img class="ldc-zoom-xl" src="${esc(ci.img)}" alt="" width="44" height="44">` : ''}</span><span>${esc(ci.name)}</span><span class="ldc-suggest__meta">${esc(theme)}</span>`,
            `href="${esc(c.url)}" data-ldc-col="${esc(c.handle)}" data-ldc-cat="${esc(ci.cat)}"`);
        }).join('');
      }
      if (prods.length) {
        html += '<p class="ldc-suggest__group" aria-hidden="true">Designs</p>' + prods.map((p) => {
          const u = p.featured_image && p.featured_image.url;
          const src = u ? `${u}${u.includes('?') ? '&' : '?'}width=120` : '';
          return opt(`<span class="ldc-suggest__thumb ldc-suggest__thumb--sq">${src ? `<img class="ldc-full" src="${esc(src)}" alt="" width="44" height="44" loading="lazy">` : ''}</span><span>${esc(p.title.split(/ [–-] /)[0])}</span><span class="ldc-suggest__meta">from $${esc(p.price)}</span>`,
            `href="${esc(p.url)}"`);
        }).join('');
      }
      html += opt(`See all results for “${esc(q)}”`, 'href="#" data-ldc-all="1"', ' ldc-suggest__all');
      box.innerHTML = html;
      box.hidden = false; input.setAttribute('aria-expanded', 'true'); active = -1;
    } catch (e) { if (e.name !== 'AbortError') closeSuggest(); }
  }

  if (form) {
    // rotating examples in the placeholder ("Try “Panthers”") until the shopper types
    const examples = (form.dataset.examples || '').split(',').map((s) => s.trim()).filter(Boolean);
    const basePh = input.placeholder;
    if (examples.length && !REDUCED) {
      let k = 0;
      setInterval(() => {
        if (input.value || document.activeElement === input) return;
        input.classList.add('is-swapping');
        setTimeout(() => { input.placeholder = `Try “${examples[k++ % examples.length]}”`; input.classList.remove('is-swapping'); }, 300);
      }, 3200);
      input.addEventListener('focus', () => { input.placeholder = basePh; });
    }
    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearBtn.hidden = !q;
      clearTimeout(sTimer);
      if (q.length < 2) return closeSuggest();
      sTimer = setTimeout(() => suggest(q), 250);
    });
    input.addEventListener('keydown', (e) => {
      const opts = box ? $$('.ldc-suggest__opt', box) : [];
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (box.hidden || !opts.length) return;
        e.preventDefault();
        active = (active + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length;
        opts.forEach((o, n) => o.classList.toggle('is-active', n === active));
        input.setAttribute('aria-activedescendant', opts[active].id);
      } else if (e.key === 'Escape') closeSuggest();
      else if (e.key === 'Enter' && active >= 0 && opts[active]) { e.preventDefault(); opts[active].click(); }
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const q = input.value.trim();
      if (!q) return;
      closeSuggest();
      input.blur();
      track('ldc_rtp_search', { search_term: q });
      apply({ q, col: '', cat: 'all' }, { push: true, scroll: true });
    });
    box.addEventListener('click', (e) => {
      const o = e.target.closest('.ldc-suggest__opt');
      if (!o) return;
      if (o.dataset.ldcAll) { e.preventDefault(); form.requestSubmit(); }
      else if (o.dataset.ldcCol) closeSuggest(); // the root click handler loads the collection in place
      else {
        track('ldc_rtp_search_product', { search_term: input.value.trim(), product: o.getAttribute('href') });
        const m = (o.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
        if (m && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button)) {
          e.preventDefault(); closeSuggest();
          openQuickAdd(decodeURIComponent(m[1]), input, 'search');
        }
      }
    });
    clearBtn.addEventListener('click', () => { clearSearch(); input.focus(); });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) closeSuggest(); });
  }

  /* ---------------- quick add: grouped size tiles, any number of sizes ---------------- */
  const qa = $('[data-ldc-qa-dialog]');
  const qaBody = $('[data-ldc-qa-body]');
  const qaTitle = $('[data-ldc-qa-title]');
  const qaThumb = $('[data-ldc-qa-thumb]');
  const qaPdp = $('[data-ldc-qa-pdp]');
  let qaOpener = null;
  const productCache = new Map();
  const GROUPS = [['Adult', 10], ['Youth', 6.5], ['Baby & toddler', 3.9], ['Hat, pocket & koozie', 0]];

  // '10.5"-Adult S-L' -> { inches: 10.5, label: '10.5″', fit: 'Adult S–L' }
  const parseSize = (t) => {
    const m = String(t).match(/(\d+(?:\.\d+)?)\s*["”″]/);
    const inches = m ? parseFloat(m[1]) : 999;
    const fit = String(t).replace(/^.*?\d+(?:\.\d+)?\s*["”″]\s*/, '').replace(/^[\s\-–—]+/, '')
      .replace(/\s*-\s*/g, '–').replace(/XLG/g, 'XL').trim();
    return { inches, label: m ? `${m[1]}″` : t, fit: fit || t };
  };

  async function getProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const url = A.productUrl ? A.productUrl(handle) : `${ROOT_URL}products/${encodeURIComponent(handle)}.js`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(res.status);
    const p = await res.json();
    productCache.set(handle, p);
    return p;
  }

  function renderQa(p) {
    const def = root.dataset.defaultSize || '10.5';
    const vs = p.variants.map((v) => ({ ...v, size: parseSize(v.public_title || v.title) })).sort((a, b) => a.size.inches - b.size.inches);
    const qty = new Map();
    const pre = vs.find((v) => v.available && (v.public_title || v.title).includes(def));
    if (pre) qty.set(pre.id, 1);
    const fi = p.featured_image ? String(p.featured_image) : '';
    qaThumb.innerHTML = '';
    const ttl = p.title.split(/ [–-] /)[0];
    qaTitle.textContent = ttl;
    // The photos a shopper would see on the product page (design on the shirt + the second mockup), shown here so they never need to leave.
    const photos = (p.images && p.images.length ? p.images.map(String) : fi ? [fi] : []).slice(0, 6);
    const at = (u, w) => `${u}${u.includes('?') ? '&' : '?'}width=${w}`;
    const gallery = photos.length ? `<div class="ldc-qa__media">
        <div class="ldc-qa__slides" role="region" aria-label="Photos of ${esc(ttl)}" tabindex="0" data-ldc-slides>
          ${photos.map((u, i) => `<img class="ldc-full" src="${esc(at(u, 900))}" srcset="${[300, 600, 900, 1200].map((w) => `${esc(at(u, w))} ${w}w`).join(', ')}" sizes="(min-width: 750px) 40vw, 130px" width="900" height="900" alt="${esc(ttl)} — photo ${i + 1} of ${photos.length}" ${i ? 'loading="lazy"' : 'fetchpriority="high"'}>`).join('')}
        </div>
        ${photos.length > 1 ? `<div class="ldc-qa__thumbs" role="group" aria-label="Choose photo">${photos.map((u, i) => `<button type="button" class="ldc-qa__thumb-btn" data-photo="${i}" aria-label="Photo ${i + 1} of ${photos.length}" aria-pressed="${i === 0}"><img class="ldc-full" src="${esc(at(u, 160))}" alt="" width="80" height="80"></button>`).join('')}</div>` : ''}
      </div>` : '';
    qaPdp.href = p.url; qaPdp.hidden = false;
    const tile = (v) => {
      const name = `${v.size.label} ${v.size.fit}`;
      return `<div class="ldc-size${v.available ? '' : ' is-off'}${qty.get(v.id) ? ' is-on' : ''}" data-id="${v.id}" data-price="${v.price}">
        <span class="ldc-size__in">${esc(v.size.label)}</span>
        <span class="ldc-size__fit">${esc(v.size.fit)}</span>
        <span class="ldc-size__price">${v.available ? money(v.price) : 'Sold out'}</span>
        <button type="button" class="ldc-size__pick" ${v.available ? '' : 'disabled'} aria-label="Add ${esc(name)}, ${money(v.price)}"></button>
        <div class="ldc-size__qty">
          <button type="button" data-step="-1" aria-label="One less ${esc(name)}">−</button>
          <output aria-live="polite">${qty.get(v.id) || 0}</output>
          <button type="button" data-step="1" aria-label="One more ${esc(name)}">+</button>
        </div>
      </div>`;
    };
    const groups = GROUPS.map(([label, min], gi) => {
      const max = gi ? GROUPS[gi - 1][1] : Infinity;
      const list = vs.filter((v) => v.size.inches >= min && v.size.inches < max);
      return list.length ? `<p class="ldc-qa__group">${label}</p><div class="ldc-qa__sizes">${list.map(tile).join('')}</div>` : '';
    }).join('');
    qaBody.innerHTML = `
      ${gallery}
      <div class="ldc-qa__panel">
        <p class="ldc-qa__hint">Tap a size to add it. Team order? Add as many sizes as you need.</p>
        ${groups}
        <div class="ldc-qa__foot">
          <div class="ldc-qa__sum"><span data-ldc-qa-n></span><strong data-ldc-qa-c></strong></div>
          <button type="button" class="ldc-btn ldc-qa__add">Add to cart</button>
          <p class="ldc-qa__msg" role="status"></p>
        </div>
      </div>`;
    const slides = $('[data-ldc-slides]', qaBody);
    if (slides) {
      const thumbs = $$('.ldc-qa__thumb-btn', qaBody);
      const mark = (i) => thumbs.forEach((b, k) => b.setAttribute('aria-pressed', String(k === i)));
      slides.addEventListener('scroll', () => mark(Math.round(slides.scrollLeft / (slides.clientWidth || 1))), { passive: true });
      thumbs.forEach((b) => b.addEventListener('click', () => {
        const i = +b.dataset.photo;
        slides.scrollTo({ left: i * slides.clientWidth, behavior: REDUCED ? 'auto' : 'smooth' });
        mark(i);
      }));
    }
    const total = () => {
      let n = 0, c = 0;
      $$('.ldc-size', qaBody).forEach((t) => { const q = qty.get(+t.dataset.id) || 0; n += q; c += q * +t.dataset.price; });
      $('[data-ldc-qa-n]', qaBody).textContent = n ? `${n} transfer${n === 1 ? '' : 's'}` : 'No sizes yet';
      $('[data-ldc-qa-c]', qaBody).textContent = money(c);
      const add = $('.ldc-qa__add', qaBody);
      add.disabled = !n;
      add.textContent = n ? 'Add to cart' : 'Pick a size';
    };
    const setQty = (t, q) => {
      const id = +t.dataset.id;
      q = Math.max(0, Math.min(999, q));
      if (q) qty.set(id, q); else qty.delete(id);
      t.classList.toggle('is-on', q > 0);
      $('output', t).textContent = q;
      total();
    };
    qaBody.onclick = (e) => {
      const t = e.target.closest('.ldc-size');
      if (!t || t.classList.contains('is-off')) return;
      if (e.target.closest('.ldc-size__pick')) { setQty(t, 1); $('[data-step="1"]', t).focus(); return; }
      const b = e.target.closest('[data-step]');
      if (b) {
        const next = (qty.get(+t.dataset.id) || 0) + +b.dataset.step;
        setQty(t, next);
        if (!next) $('.ldc-size__pick', t).focus();
      }
    };
    $('.ldc-qa__add', qaBody).addEventListener('click', () => addToCart(qty, p));
    total();
  }

  async function addToCart(qty, p) {
    const items = Array.from(qty, ([id, quantity]) => ({ id, quantity })).filter((i) => i.quantity);
    if (!items.length) return;
    const btn = $('.ldc-qa__add', qaBody);
    const msg = $('.ldc-qa__msg', qaBody);
    btn.disabled = true; btn.setAttribute('aria-busy', 'true'); btn.textContent = 'Adding…'; msg.textContent = '';
    const cn = document.querySelector('cart-notification');
    const sections = cn && cn.getSectionsToRender ? cn.getSectionsToRender().map((s) => s.id) : [];
    try {
      const json = A.addToCart ? await A.addToCart(items) : await fetch(`${(window.routes && window.routes.cart_add_url) || ROOT_URL + 'cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname }),
      }).then((r) => r.json());
      if (json.status) throw new Error(json.description || json.message || 'Could not add to cart');
      const n = items.reduce((a, i) => a + i.quantity, 0);
      track('ldc_rtp_add_to_cart', { product: p.handle, sizes: items.length, quantity: n });
      qa.close();
      const added = json.items || [json];
      if (cn && json.sections) cn.renderContents({ ...json, key: added[0].key });
      else if (!A.addToCart) location.href = `${ROOT_URL}cart`;
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ldc-rtp', productVariantId: items[0].id, cartData: json });
    } catch (err) {
      msg.textContent = err.message;
      btn.textContent = 'Add to cart';
    } finally {
      btn.disabled = false; btn.removeAttribute('aria-busy');
    }
  }

  async function openQuickAdd(handle, opener, via) {
    qaOpener = opener;
    qaTitle.textContent = 'Loading sizes…'; qaThumb.innerHTML = ''; qaPdp.hidden = true;
    qaBody.innerHTML = '<div class="ldc-qa__loading" aria-hidden="true"></div>';
    if (!qa.open) qa.showModal();
    track('ldc_rtp_quick_add', { product: handle, via: via || 'plus' });
    try {
      renderQa(await getProduct(handle));
      // Focus a button, never a text field — an input would pop the phone keyboard.
      const on = $('.ldc-size.is-on [data-step="1"]', qaBody) || $('.ldc-size__pick:not([disabled])', qaBody);
      (on || $('[data-ldc-qa-close]')).focus({ preventScroll: true });
    } catch (err) {
      qaBody.innerHTML = `<p>We couldn't load sizes. <a href="${ROOT_URL}products/${esc(handle)}">Open the product page</a>.</p>`;
    }
  }

  root.addEventListener('click', (e) => {
    const plus = e.target.closest('[data-ldc-qa]');
    if (plus) return openQuickAdd(plus.dataset.ldcQa, plus, 'plus');
    // Clicking the design itself (picture or name) opens the same size sheet: shoppers stay on the page.
    // Ctrl/Cmd/Shift-click or middle-click still opens the product page normally; sold-out designs (no "+") link through.
    const link = e.target.closest('.ldc-card__imglink, .ldc-card__link');
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
    const card = link.closest('.ldc-card');
    const btn = card && card.querySelector('[data-ldc-qa]');
    if (!btn) return;
    e.preventDefault();
    openQuickAdd(btn.dataset.ldcQa, btn, 'card');
  });
  $('[data-ldc-qa-close]').addEventListener('click', () => qa.close());
  qa.addEventListener('click', (e) => { if (e.target === qa) qa.close(); });
  qa.addEventListener('close', () => qaOpener && qaOpener.focus({ preventScroll: true }));

  /* ---------------- boot ---------------- */
  function fromUrl(push) {
    const sp = new URLSearchParams(location.search);
    const q = sp.get('q') || '';
    if (input) { input.value = q; clearBtn.hidden = !q; }
    const col = sp.get('col') || '';
    const styleHandle = sp.get('style') || '';
    const style = styleHandle && colIndex[col] ? colIndex[col].styles.find((s) => handleize(s) === styleHandle) || '' : '';
    const cat = sp.get('cat') || (sp.toString() ? 'all' : root.dataset.defaultCat || 'all');
    state.cat = ''; // force the theme's default sort to apply on first load
    apply({ cat, col, style, q, sort: sp.get('sort') || '' }, { push });
  }
  root.classList.add('is-ready');
  const sp0 = new URLSearchParams(location.search);
  if (['cat', 'col', 'q', 'sort', 'style'].some((k) => sp0.has(k)) || (root.dataset.defaultCat && root.dataset.defaultCat !== 'all')) fromUrl(false);
  else syncNav();
})();
